import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { Button } from '@/ui/Button'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { WebhooksCard } from './WebhooksCard'

type Preference = Schema<'PreferenceOut'>
type ChannelState = Schema<'ChannelStateOut'>

/**
 * Settings → Notifications — S-D3's table, converted, plus webhooks.
 *
 * **The table is the design's, row for row.** Event down the side, channel
 * across the top, an em dash where a channel does not apply to an event. What
 * S-D3 draws as three columns is four here, because REQUIREMENTS §7 names
 * WhatsApp **and SMS** together as the paid add-on and a column for one of them
 * would have made the other unreachable.
 *
 * **Every row comes from the server, including the ones nothing emits yet.**
 * The catalogue lives in `app/notifications/events.py`; reassembling the
 * defaults here would be a second copy of it in TypeScript, which is the drift
 * REQUIREMENTS §9.1 forbids hand-written API types to prevent. Events no code
 * raises yet carry the session that will raise them, and are rendered with that
 * note — the same treatment the sidebar gives a module that has not shipped.
 *
 * **Preferences are per organisation, not per person**, so the footer says so.
 * That is why saving needs `notifications:manage` while reading does not: one
 * admin turning email off turns it off for their colleagues.
 *
 * **Webhooks are a second card on this panel** rather than a ninth entry in the
 * settings navigation. S-D3 drew eight panels and none of them is webhooks, so
 * adding one to the approved navigation would be inventing information
 * architecture; putting the endpoints beside the channel switches — which is
 * what they are, a channel this organisation configured itself — uses only the
 * approved components and leaves the panel list as designed.
 */

const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-app',
  email: 'Email',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
}

const CHANNEL_ORDER = ['in_app', 'email', 'whatsapp', 'sms'] as const

type Draft = Record<string, Record<string, boolean>>

function toDraft(preferences: Preference[]): Draft {
  const draft: Draft = {}
  for (const row of preferences) {
    draft[row.event] = Object.fromEntries(
      row.channels.map((channel) => [channel.channel, channel.enabled]),
    )
  }
  return draft
}

export function NotificationsPanel(): JSX.Element {
  const toast = useToast()
  const [draft, setDraft] = useState<Draft | null>(null)

  const preferences = useQuery(
    (signal) => call(() => api.GET('/api/v1/notifications/preferences', { signal })),
    [],
  )

  const save = useMutation((body: Draft) =>
    call(() =>
      api.PUT('/api/v1/notifications/preferences', {
        body: {
          preferences: Object.entries(body).map(([event, channels]) => ({
            event,
            channels: {
              in_app: channels.in_app ?? false,
              email: channels.email ?? false,
              whatsapp: channels.whatsapp ?? false,
              sms: channels.sms ?? false,
            },
          })),
        },
      }),
    ),
  )

  // The draft mirrors the server until the first edit, and is re-seeded whenever
  // the server answers again. Rendering straight from the query would make every
  // switch a round trip; keeping a draft that never re-seeded would show a stale
  // table after a save from another tab.
  useEffect(() => {
    if (preferences.state.status === 'ready') setDraft(toDraft(preferences.state.data.preferences))
  }, [preferences.state])

  const rows = preferences.state.data?.preferences ?? []
  const paid = preferences.state.data?.paid_channels ?? false
  const dirty =
    draft !== null &&
    preferences.state.status === 'ready' &&
    JSON.stringify(draft) !== JSON.stringify(toDraft(preferences.state.data.preferences))

  function toggle(event: string, channel: string, value: boolean): void {
    setDraft((current) =>
      current === null ? current : { ...current, [event]: { ...current[event], [channel]: value } },
    )
  }

  return (
    <div className="ar-stack">
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Notifications</h3>
          <span className="ar-card__spacer" />
          {/* Only once the plan is known: rendering it while the query is in
              flight would claim the add-on is absent before anything has said so. */}
          {preferences.state.status === 'ready' && !paid ? (
            <span className="ar-badge ar-badge--info">WhatsApp and SMS are an add-on</span>
          ) : null}
        </div>

        <InlineError failure={save.state.failure} />

        {preferences.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading notification settings" lines={5} />
          </div>
        ) : null}

        {preferences.state.status === 'error' ? (
          <ErrorState
            failure={preferences.state.failure}
            title="Could not load notification settings"
            onRetry={preferences.refetch}
          />
        ) : null}

        {preferences.state.status === 'ready' && draft !== null ? (
          <>
            <div className="ar-table-wrap">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    {CHANNEL_ORDER.map((channel) => (
                      <th key={channel}>{CHANNEL_LABELS[channel]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.event}>
                      <td>
                        <strong>{row.label}</strong>
                        {row.note ? (
                          <div
                            className="ar-subtle"
                            style={{ fontSize: 'var(--ar-text-caption-size)' }}
                          >
                            {row.note}
                          </div>
                        ) : null}
                        {row.live ? null : (
                          <div
                            className="ar-subtle"
                            style={{ fontSize: 'var(--ar-text-caption-size)' }}
                          >
                            Arrives in {row.emitted_by}
                          </div>
                        )}
                      </td>
                      {CHANNEL_ORDER.map((channel) => (
                        <td key={channel}>
                          <ChannelSwitch
                            state={row.channels.find((item) => item.channel === channel)}
                            label={`${CHANNEL_LABELS[channel] ?? channel} for ${row.label}`}
                            checked={draft[row.event]?.[channel] ?? false}
                            onChange={(value) => {
                              toggle(row.event, channel, value)
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ar-card__footer">
              <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                These apply to everyone in this organisation. Transactional messages — password
                resets, security alerts — cannot be turned off.
              </span>
              <span className="ar-card__spacer" />
              <Button
                tone="primary"
                busy={save.pending}
                busyLabel="Saving"
                disabled={!dirty}
                onClick={() => {
                  void (async () => {
                    const result = await save.run(draft)
                    if (!result.ok) return
                    preferences.refetch()
                    toast.show({ kind: 'success', title: 'Notification settings saved' })
                  })()
                }}
              >
                Save
              </Button>
            </div>
          </>
        ) : null}
      </section>

      <WebhooksCard />
    </div>
  )
}

/**
 * One cell.
 *
 * An em dash where the event has no such channel — S-D3's own treatment for the
 * weekly summary's in-app and WhatsApp cells — and a disabled switch, with the
 * reason beside it, where the plan does not include the channel. Disabled rather
 * than hidden, so a tenant can see what the add-on would give them.
 */
function ChannelSwitch({
  state,
  label,
  checked,
  onChange,
}: {
  state: ChannelState | undefined
  label: string
  checked: boolean
  onChange: (value: boolean) => void
}): JSX.Element {
  if (!state?.available) return <span aria-hidden="true">—</span>
  // The reason goes in the accessible name rather than in a `title` on the
  // label: a wrapping label's `title` *becomes* the control's accessible name
  // when the label has no text, which would have made every paid switch read as
  // "Not on your plan" to a screen reader and to a test.
  const name = state.included_in_plan ? label : `${label} (not on your plan)`
  return (
    <label className="ar-switch">
      <input
        type="checkbox"
        aria-label={name}
        checked={checked}
        disabled={!state.included_in_plan}
        onChange={(event) => {
          onChange(event.currentTarget.checked)
        }}
      />
      <span className="ar-switch__track" />
    </label>
  )
}
